// AddProduct.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./AdminProducts.css";
import { toast } from "react-toastify";
import RichTextEditor from "./RichTextEditor";

const API_URL = process.env.REACT_APP_API_URL;

const AddProduct = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState("");

  const [product, setProduct] = useState({
    name: "",
    description: "",
    price: "",
    old_price: "",
    category_id: "",
    stock_quantity: "",
    sku: "",
    product_code: "",
    is_featured: false,
    is_active: true,
    color: "",
    weight: "",
    length: "",
    width: "",
    height: ""
  });

  const [mainImage, setMainImage] = useState(null);
  const [mainPreview, setMainPreview] = useState(null);
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryPreview, setGalleryPreview] = useState([]);

  const fetchNextPID = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/admin/products/next-id`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProduct(prev => ({ ...prev, product_code: res.data.nextId }));
    } catch (err) {
      console.error("PID fetch error", err);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchNextPID();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/admin/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(res.data.categories);
    } catch (err) {
      toast.error("Failed to load categories");
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProduct(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
    setProduct(prev => ({ ...prev, description: value }));
  };

  const handleMainImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }
    setMainImage(file);
    setMainPreview(URL.createObjectURL(file));
  };

  const handleVideo = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("video/")) {
      toast.error("Please upload a video file");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Video size should be less than 50MB");
      return;
    }
    setVideo(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const handleGalleryImages = (e) => {
    const files = Array.from(e.target.files);

    if (galleryImages.length + files.length > 10) {
      toast.warning("You can upload up to 10 images. Please select fewer.");
      return;
    }

    const validFiles = files.filter(file => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} size should be less than 5MB`);
        return false;
      }
      return true;
    });

    setGalleryImages(prev => [...prev, ...validFiles]);
    const previews = validFiles.map(file => URL.createObjectURL(file));
    setGalleryPreview(prev => [...prev, ...previews]);
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
    setGalleryPreview(prev => prev.filter((_, i) => i !== index));
  };

  const removeMainImage = () => {
    setMainImage(null);
    if (mainPreview) {
      URL.revokeObjectURL(mainPreview);
    }
    setMainPreview(null);
  };

  const removeVideo = () => {
    setVideo(null);
    if (videoPreview) {
      URL.revokeObjectURL(videoPreview);
    }
    setVideoPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!product.name.trim()) return toast.error("Product name is required");
    if (!mainImage) return toast.error("Main image is required");
    if (!product.category_id) return toast.error("Category is required");
    if (!product.price || product.price <= 0) return toast.error("Valid price is required");
    if (!product.sku.trim()) return toast.error("SKU is required");
    if (!product.product_code.trim()) return toast.error("Product code is required");

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      const formData = new FormData();
      formData.append("name", product.name.trim());
      formData.append("description", product.description || "");
      formData.append("price", Number(product.price));
      formData.append("category_id", Number(product.category_id));
      formData.append("sku", product.sku.trim());
      formData.append("product_code", product.product_code.trim());
      if (product.old_price) formData.append("old_price", Number(product.old_price));
      if (product.stock_quantity) formData.append("stock_quantity", Number(product.stock_quantity));
      formData.append("is_featured", product.is_featured);
      formData.append("is_active", product.is_active);
      formData.append("image", mainImage);
      if (video) formData.append("video", video);
      formData.append("color", product.color || "");
      if (product.weight) formData.append("weight", product.weight);
      if (product.length) formData.append("length", product.length);
      if (product.width) formData.append("width", product.width);
      if (product.height) formData.append("height", product.height);

      const res = await axios.post(`${API_URL}/admin/products`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      if (galleryImages.length > 0) {
        const productId = res.data.product.id;
        const imgData = new FormData();
        galleryImages.forEach(img => imgData.append("images", img));
        await axios.post(`${API_URL}/admin/products/${productId}/images`, imgData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      toast.success("Product added successfully");
      navigate("/admin/products");
    } catch (err) {
      console.error("Product creation error:", err);
      toast.error(err.response?.data?.message || "Product creation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-product-container">
      <div className="form-header">
        <h2 className="page-title">Add New Product</h2>
        <button className="back-btn" onClick={() => navigate("/admin/products")}>
          ← Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="premium-form">
        <div className="form-section">
          <h4 className="section-title">General Information</h4>
          <div className="form-group">
            <label>Product Name <span className="required">*</span></label>
            <input
              type="text"
              name="name"
              placeholder="e.g. Premium Silk Saree"
              value={product.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>SKU (Stock Keeping Unit) <span className="required">*</span></label>
              <input
                type="text"
                name="sku"
                placeholder="e.g. S-001-WHITE"
                value={product.sku}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Product ID (Serial) <span className="required">*</span></label>
              <input
                type="text"
                name="product_code"
                value={product.product_code}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category <span className="required">*</span></label>
              <select name="category_id" value={product.category_id} onChange={handleChange} required>
                <option value="">Select Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Color</label>
              <input
                type="text"
                name="color"
                placeholder="e.g. Red, Blue, Multicolor"
                value={product.color}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Description</label>
            <RichTextEditor
              value={description}
              onChange={handleDescriptionChange}
              placeholder="Write a detailed description of the product..."
              height={300}
            />
          </div>
        </div>

        <div className="form-section">
          <h4 className="section-title">Pricing & Stock</h4>
          <div className="form-row">
            <div className="form-group">
              <label>Selling Price (₹) <span className="required">*</span></label>
              <input type="number" step="0.01" name="price" placeholder="0.00" value={product.price} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Old Price (₹)</label>
              <input type="number" step="0.01" name="old_price" placeholder="0.00" value={product.old_price} onChange={handleChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Stock Quantity</label>
            <input type="number" name="stock_quantity" placeholder="0" value={product.stock_quantity} onChange={handleChange} />
          </div>
        </div>

        <div className="form-section">
          <h4 className="section-title">Shipping Details (Shiprocket)</h4>
          <p className="section-subtitle">Leave blank to use defaults (0.7kg, 30x20x5cm)</p>
          <div className="form-row">
            <div className="form-group">
              <label>Weight (kg)</label>
              <input type="number" step="0.01" name="weight" placeholder="0.7" value={product.weight} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Length (cm)</label>
              <input type="number" step="0.1" name="length" placeholder="30" value={product.length} onChange={handleChange} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Width (cm)</label>
              <input type="number" step="0.1" name="width" placeholder="20" value={product.width} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Height (cm)</label>
              <input type="number" step="0.1" name="height" placeholder="5" value={product.height} onChange={handleChange} />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4 className="section-title">Images & Video</h4>

          <div className="form-row">
            <div className="form-group">
              <label>Main Image <span className="required">*</span></label>
              <div className="image-upload-grid">
                <div className="upload-box main-upload">
                  <input type="file" accept="image/*" onChange={handleMainImage} id="main-image-input" hidden />
                  <label htmlFor="main-image-input" className="preview-label">
                    {mainPreview ? (
                      <div className="image-preview-wrapper">
                        <img src={mainPreview} alt="Preview" className="preview-img" />
                        <button type="button" className="remove-image-btn" onClick={removeMainImage}>
                          <i className="bi bi-x-circle"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <i className="bi bi-cloud-arrow-up"></i>
                        <span>Select Main Image</span>
                        <small>JPG, PNG, WEBP (Max 5MB)</small>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Product Video (Optional)</label>
              <div className="image-upload-grid">
                <div className="upload-box main-upload">
                  <input type="file" accept="video/*" onChange={handleVideo} id="video-input" hidden />
                  <label htmlFor="video-input" className="preview-label">
                    {videoPreview ? (
                      <div className="video-preview-wrapper">
                        <video src={videoPreview} className="preview-img" muted />
                        <div className="video-overlay"><i className="bi bi-play-circle"></i></div>
                        <button type="button" className="remove-video-btn" onClick={removeVideo}>
                          <i className="bi bi-x-circle"></i>
                        </button>
                      </div>
                    ) : (
                      <div className="upload-placeholder">
                        <i className="bi bi-play-btn"></i>
                        <span>Select Video</span>
                        <small>MP4, WebM (Max 50MB)</small>
                      </div>
                    )}
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Gallery Images (Optional - Max 10 images)</label>
            <div className="image-upload-grid gallery-grid">
              {galleryPreview.map((img, index) => (
                <div key={index} className="gallery-item">
                  <img src={img} alt={`Gallery ${index + 1}`} />
                  <button type="button" className="remove-img" onClick={() => removeGalleryImage(index)}>
                    <i className="bi bi-x-circle"></i>
                  </button>
                </div>
              ))}
              {galleryPreview.length < 10 && (
                <div className="upload-box">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleGalleryImages}
                    id="gallery-image-input"
                    hidden
                  />
                  <label htmlFor="gallery-image-input" className="upload-placeholder">
                    <i className="bi bi-plus-lg"></i>
                    <span>Add Images</span>
                    <small>{galleryPreview.length}/10</small>
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h4 className="section-title">Visibility & Status</h4>
          <div className="toggle-group">
            <label className="toggle-item">
              <input type="checkbox" name="is_featured" checked={product.is_featured} onChange={handleChange} />
              <span>Mark as Featured</span>
            </label>
            <label className="toggle-item">
              <input type="checkbox" name="is_active" checked={product.is_active} onChange={handleChange} />
              <span>Active on Website</span>
            </label>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? (
              <>
                <i className="bi bi-hourglass-split"></i> Creating Product...
              </>
            ) : (
              <>Create Product</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddProduct;